import { useColorScheme } from '@vkontakte/vkui';
import { MouseEventHandler, useCallback, useLayoutEffect, useRef } from 'react';
import { getDevicePixelRatio } from '@djs';
import { useEqual } from '@dui/hooks';
import { useDidUpdate } from '@djs/react';
import { CustomTouchEventHandler } from '@dui/vkuiReexports';
import { calcPreparedValues, drawValues, initCanvas } from './helpers';
import { PreparedValues, UseAudioWaveformParams } from './types';
import { DEFAULT_LINE_WIDTH, DEFAULT_LINE_GAP } from './const';

export function useAudioWaveform(params: UseAudioWaveformParams) {
  const {
    isInteractDisabled,
    waveform,
    getProgressSetterRef,
    onProgressMove,
    onProgressMoveEnd,
    onProgressMoveStart,
    className,
    lineGap = DEFAULT_LINE_GAP,
    lineWidth = DEFAULT_LINE_WIDTH,
    waveformMaxValue,
  } = params;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const preparedValuesRef = useRef<PreparedValues>(null);
  const isTouchInProgressRef = useRef<{ fromProgress: number }>(null);

  const currentProgressRef = useRef(0);

  const updateWave = useCallback(
    (percent: number) => {
      const canvasEl = canvasRef.current;
      const preparedValues = preparedValuesRef.current;

      if (!canvasEl || !preparedValues) {
        return;
      }

      drawValues(canvasEl, preparedValues, { percent, lineGap, lineWidth });
      canvasEl.setAttribute('aria-valuenow', `${percent}`);
    },
    [lineGap, lineWidth],
  );

  const startXHandler: CustomTouchEventHandler = (e) => {
    if (isInteractDisabled) {
      return;
    }

    // исключаем конфликт с ios swipeback и message swipe reply
    e.originalEvent.stopPropagation();
    // исключаем конфликт со скроллом
    e.originalEvent.preventDefault();

    onProgressMoveStart?.();
    isTouchInProgressRef.current = { fromProgress: currentProgressRef.current };
  };

  const moveXHandler: CustomTouchEventHandler = (e) => {
    if (!preparedValuesRef.current || isInteractDisabled || !isTouchInProgressRef.current) {
      return;
    }

    const parentWidth = preparedValuesRef.current.width;
    const percent = isTouchInProgressRef.current.fromProgress + e.shiftX / parentWidth;

    // исключаем конфликт с ios swipeback и message swipe reply
    e.originalEvent.stopPropagation();
    // исключаем конфликт со скроллом
    e.originalEvent.preventDefault();

    onProgressMove?.(percent);
  };

  const endXHandler: CustomTouchEventHandler = (e) => {
    if (!preparedValuesRef.current || isInteractDisabled || !isTouchInProgressRef.current) {
      return;
    }

    const parentWidth = preparedValuesRef.current.width;
    const percent = isTouchInProgressRef.current.fromProgress + e.shiftX / parentWidth;

    onProgressMoveEnd?.(percent);
    isTouchInProgressRef.current = null;
  };

  const onClick: MouseEventHandler<HTMLElement> = (e) => {
    if (!preparedValuesRef.current || isInteractDisabled || !canvasRef.current || !isTouchInProgressRef.current) {
      return;
    }

    const parentWidth = preparedValuesRef.current.width;
    const percent = (e.clientX - preparedValuesRef.current.left) / parentWidth;

    isTouchInProgressRef.current = null;
    updateWave(percent);
    onProgressMoveEnd?.(percent);
  };

  const equalsWaveform = useEqual(waveform);

  useLayoutEffect(() => {
    getProgressSetterRef.current = (percent) => {
      currentProgressRef.current = percent;
      updateWave(currentProgressRef.current);
    };

    return () => {
      getProgressSetterRef.current = null;
    };
  }, [getProgressSetterRef, updateWave]);

  useLayoutEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) {
      return;
    }

    requestAnimationFrame(() => {
      // Адаптируем имеющийся, уже нормализованный массив waveform, относительно канвас контейнера
      // Считаем, что максимум при предыдущей нормализации(waveformParams.values.max),
      // должен быть нормализован к максимальной высоте контейнера,
      // и все элементы масива нормализованы пропорционально этому правилу.
      preparedValuesRef.current =
        calcPreparedValues(canvasEl, equalsWaveform, lineWidth, lineGap, waveformMaxValue) || null;
      if (preparedValuesRef.current) {
        initCanvas(canvasEl, preparedValuesRef.current, getDevicePixelRatio());
        updateWave(currentProgressRef.current);
      }
    });
  }, [updateWave, equalsWaveform, lineWidth, lineGap, waveformMaxValue]);

  const colorScheme = useColorScheme();

  useDidUpdate(() => {
    updateWave(currentProgressRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateWave, colorScheme]);

  return {
    moveXHandler,
    startXHandler,
    endXHandler,
    onClick,
    canvasRef,
    className,
  };
}
